import { useState } from "react";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";

export interface FaqItem {
  id: string;
  question_en: string;
  question_tr: string;
  answer_en: string;
  answer_tr: string;
  sort_order: number;
  links?: string | any[];
}

export function safeParseLinks(links: any): any[] {
  if (!links) return [];
  if (Array.isArray(links)) return links;
  try {
    const parsed = typeof links === "string" ? JSON.parse(links) : links;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useFaqsDraft(
  editingFaq: Partial<FaqItem> | null,
  setEditingFaq: React.Dispatch<React.SetStateAction<Partial<FaqItem> | null>>,
  faqs: FaqItem[],
  activeDraft: any,
  setActiveDraft: React.Dispatch<React.SetStateAction<any>>
) {
  const handleSaveDraft = async () => {
    if (!editingFaq) return;
    try {
      const draftPayload = {
        ...editingFaq,
        links: typeof editingFaq.links === "string" ? editingFaq.links : JSON.stringify(editingFaq.links || [])
      };
      await adminFetch("/api/admin/drafts", {
        operation: "save",
        item_type: "faq",
        item_id: editingFaq.id,
        draft_data: draftPayload,
      });
      setActiveDraft({
        ...draftPayload,
        links: safeParseLinks(draftPayload.links)
      });
      toast.success("FAQ draft saved to staging successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to save draft");
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingFaq) return;
    if (!confirm("Are you sure you want to discard this staged draft?")) return;
    try {
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "faq",
        item_id: editingFaq.id,
      });
      setActiveDraft(null);
      const live = faqs.find((f) => f.id === editingFaq.id);
      setEditingFaq(live || null);
      toast.success("Staged draft discarded successfully!");
    } catch (err: any) {
      toast.error(err.message || "Failed to discard draft");
    }
  };

  return { handleSaveDraft, handleDiscardDraft };
}

async function swapFaqOrder(faqA: FaqItem, faqB: FaqItem, onRefresh: () => void) {
  try {
    await adminFetch("/api/admin/faqs", { ...faqA, sort_order: faqB.sort_order });
    await adminFetch("/api/admin/faqs", { ...faqB, sort_order: faqA.sort_order });
    onRefresh();
  } catch (err: any) {
    toast.error(err.message || "Failed to reorder items");
  }
}

const createDefaultFaq = (faqsLength: number): Partial<FaqItem> => ({
  id: `faq-${Date.now()}`,
  question_en: "",
  question_tr: "",
  answer_en: "",
  answer_tr: "",
  sort_order: faqsLength,
  links: [],
});

export function useFaqsManager(faqs: FaqItem[], onRefresh: () => void) {
  const [editingFaq, setEditingFaq] = useState<Partial<FaqItem> | null>(null);
  const [activeDraft, setActiveDraft] = useState<any>(null);

  const { handleSaveDraft, handleDiscardDraft } = useFaqsDraft(
    editingFaq,
    setEditingFaq,
    faqs,
    activeDraft,
    setActiveDraft
  );

  const startAdd = () => {
    setActiveDraft(null);
    setEditingFaq(createDefaultFaq(faqs.length));
  };

  const handleEdit = (faq: FaqItem) => {
    const parsedFaq = {
      ...faq,
      links: safeParseLinks(faq.links)
    };
    setEditingFaq(parsedFaq);
    adminFetch(`/api/admin/drafts?item_type=faq&item_id=${faq.id}`)
      .then((res) => {
        if (res?.success && res.draft) {
          const draftParsed = {
            ...res.draft,
            links: safeParseLinks(res.draft.links)
          };
          setActiveDraft(draftParsed);
          setEditingFaq(draftParsed);
          toast.info("Unpublished staged draft loaded for editing.");
        } else {
          setActiveDraft(null);
        }
      })
      .catch(() => setActiveDraft(null));
  };

  const handleSave = async () => {
    if (!editingFaq?.question_en || !editingFaq?.answer_en) {
      toast.error("Question and Answer (English) are required!");
      return;
    }
    try {
      const payload = {
        ...editingFaq,
        links: typeof editingFaq.links === "string" ? JSON.parse(editingFaq.links) : (editingFaq.links || [])
      };
      await adminFetch("/api/admin/drafts", {
        operation: "promote",
        item_type: "faq",
        item_id: editingFaq.id,
        draft_data: payload,
      });
      toast.success("FAQ item saved successfully!");
      setEditingFaq(null);
      setActiveDraft(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to save FAQ item");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this FAQ item?")) return;
    try {
      await adminFetch("/api/admin/faqs", { operation: "delete", id });
      await adminFetch("/api/admin/drafts", {
        operation: "delete",
        item_type: "faq",
        item_id: id,
      });
      toast.success("FAQ item deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete FAQ item");
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    const sortedFaqs = [...faqs].sort((a, b) => a.sort_order - b.sort_order);
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= sortedFaqs.length) return;

    const faqA = sortedFaqs[index];
    const faqB = sortedFaqs[swapIndex];
    if (faqA && faqB) {
      await swapFaqOrder(faqA, faqB, onRefresh);
    }
  };

  return {
    editingFaq,
    setEditingFaq,
    activeDraft,
    setActiveDraft,
    startAdd,
    handleEdit,
    handleSaveDraft,
    handleDiscardDraft,
    handleSave,
    handleDelete,
    handleMove
  };
}

export default useFaqsManager;
